import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  WarningOutlined
} from "@ant-design/icons"
import { Button, Card, Flex, Tag, Typography } from "antd"
import clsx from "clsx"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../../constants/placeholders.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  COMPLIANCE_STATUS_COLOR_MAP,
  TRACKED_COMPLIANCE_DOCUMENT_TYPES
} from "../../constants/compliance.ts"
import {
  type SubcontractorComplianceDocument,
  type SubcontractorComplianceDocumentType,
  type SubcontractorComplianceState
} from "../../types/types.ts"

const { Text } = Typography

type ComplianceEntry = [
  string,
  (
    | { status: SubcontractorComplianceState; expiration_date: string | null }
    | undefined
  )
]

const STATUS_ICONS: Record<SubcontractorComplianceState, React.ReactNode> = {
  valid: <CheckCircleOutlined />,
  expiring_soon: <ClockCircleOutlined />,
  expired: <ExclamationCircleOutlined />,
  no_expiry: null
}

interface Props {
  entries: ComplianceEntry[]
  documentByType: (
    docType: string
  ) => SubcontractorComplianceDocument | undefined
  /** The general subcontractor_edit right, which gates View as well. */
  isEditable: boolean
  /** subcontractor_compliance_edit: what Upload/Replace actually needs. */
  mayEditCompliance: boolean
  /** The document an expiry alert deep-linked to, if any. */
  highlightedDocument: string | null
  onUpload: (docType: SubcontractorComplianceDocumentType) => void
}

/**
 * One card per document, so its name, status, date and actions read together.
 * A missing document is coloured, bordered and badged so it is identifiable
 * without reading every card; the count beside the title is derived from the
 * same statuses the cards show.
 */
export const ComplianceDocumentsPanel = ({
  entries,
  documentByType,
  isEditable,
  mayEditCompliance,
  highlightedDocument,
  onUpload
}: Props) => {
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()

  const missingCount = entries.filter(([, compliance]) => !compliance).length

  return (
    <Card
      size={"small"}
      title={
        <Flex align={"center"} gap={12} wrap>
          <span>{t("subcontractorDetail.complianceDocuments")}</span>
          {missingCount > 0 && (
            <Text className={"text-sm font-normal text-red-600"}>
              {t("subcontractorDetail.compliance.missingCount", {
                count: missingCount
              })}
            </Text>
          )}
        </Flex>
      }
    >
      <div className={"grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"}>
        {entries.map(([docType, compliance]) => {
          const tracked = TRACKED_COMPLIANCE_DOCUMENT_TYPES.includes(
            docType as SubcontractorComplianceDocumentType
          )
          const document = tracked ? documentByType(docType) : undefined
          const missing = !compliance
          const expiresOn = compliance?.expiration_date
            ? parseDate(compliance.expiration_date)?.format(
                userFormat || "YYYY-MM-DD"
              )
            : null

          return (
            <Flex
              vertical
              gap={8}
              key={docType}
              // The card an expiry alert pointed at. The data attribute is
              // what the tests read: a Tailwind background is invisible to
              // jsdom, so asserting on the colour would pass either way.
              data-document-type={docType}
              data-highlighted={docType === highlightedDocument || undefined}
              className={clsx(
                "rounded-lg border border-solid p-3",
                missing
                  ? "border-red-200 bg-red-50"
                  : "border-gray-200 bg-white",
                // A ring rather than another background: the card an alert
                // pointed at may also be a missing one, whose red must stay.
                docType === highlightedDocument && "ring-2 ring-amber-400"
              )}
            >
              <Flex component={"span"} align={"center"} gap={8}>
                <span
                  aria-hidden
                  className={clsx(
                    "flex size-8 shrink-0 items-center justify-center rounded-full",
                    missing
                      ? "bg-red-100 text-red-500"
                      : "bg-gray-100 text-gray-500"
                  )}
                >
                  {missing ? (
                    <WarningOutlined />
                  ) : (
                    <SafetyCertificateOutlined />
                  )}
                </span>
                <Text strong className={"break-words"}>
                  {COMPLIANCE_DOCUMENT_LABELS[docType] || docType}
                </Text>
              </Flex>

              {compliance ? (
                <>
                  <Tag
                    className={"w-fit"}
                    icon={STATUS_ICONS[compliance.status]}
                    color={
                      COMPLIANCE_STATUS_COLOR_MAP[compliance.status] ??
                      "default"
                    }
                  >
                    {t(`subcontractorCompliance.status.${compliance.status}`, {
                      defaultValue: compliance.status
                    })}
                  </Tag>
                  <Text type={"secondary"} className={"text-xs"}>
                    {expiresOn ? (
                      <>
                        {`${t("subcontractorDetail.compliance.expires")} `}
                        <Text type={"secondary"} className={"text-xs"}>
                          {expiresOn}
                        </Text>
                      </>
                    ) : (
                      empty
                    )}
                  </Text>
                </>
              ) : (
                <>
                  <Tag
                    className={"w-fit"}
                    color={"error"}
                    icon={<WarningOutlined />}
                  >
                    {t("subcontractorDetail.compliance.missing")}
                  </Tag>
                  <Text type={"secondary"} className={"text-xs"}>
                    {t("subcontractorDetail.compliance.noDocumentUploaded")}
                  </Text>
                </>
              )}

              {tracked && isEditable && (
                <Flex component={"span"} gap={8} align={"center"} wrap>
                  {document?.file && (
                    <a
                      href={document.file}
                      target={"_blank"}
                      rel={"noreferrer"}
                    >
                      {t("subcontractorDetail.compliance.view")}
                    </a>
                  )}
                  {/* `upload_compliance_document` wants
                      `subcontractor_compliance_edit`, so general edit rights
                      no longer carry it. Viewing a document that is already on
                      file is a read and stays. */}
                  {mayEditCompliance && (
                    <Button
                      size={"small"}
                      onClick={() =>
                        onUpload(docType as SubcontractorComplianceDocumentType)
                      }
                    >
                      {document
                        ? t("subcontractorDetail.compliance.replace")
                        : t("subcontractorDetail.compliance.upload")}
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          )
        })}
      </div>
    </Card>
  )
}
